// services/operationsDashboardService.ts

import { EndpointKeys } from "@/config/api";
import ApiService from "@/services/api";
import {
    EmployerSummary,
    EmployerDetail,
    OperationalIssueSummary,
    OperationalIssueDetail,
    IssueDetail,
    IssuesByCategory,
    OverrideIssueRequest,
    AffiliatePartner,
    AffiliatePartnersResponse,
    ImplementationEmployer,
    ImplementationEmployersResponse,
    OnboardingStepOverride,
    OnboardingStepType,
    CreateOverrideRequest
} from "@/types/operations";
import {
    DemoAccountsResponse,
    DemoAccountsSummary,
    DemoEmailList
} from "@/types/demo";

interface Feature {
    id: number;
    key: string;
    description: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

/**
 * Service for handling operations dashboard API calls
 */
export const OperationsDashboardService = {
    /**
     * Get list of employers with summary data
     */
    async getEmployerList(): Promise<EmployerSummary[]> {
        try {
            const data = await ApiService.request<{ employers: EmployerSummary[] }>(
                EndpointKeys.GET_EMPLOYER_LIST
            );
            return data.employers;
        } catch (error) {
            console.error('Error fetching employer list:', error);
            throw new Error("Failed to fetch employer list");
        }
    },

    /**
     * Get detailed info for a specific employer
     */
    async getEmployerDetail(employerId: number): Promise<EmployerDetail> {
        try {
            const data = await ApiService.request<{ employer_details: EmployerDetail }>(
                EndpointKeys.GET_EMPLOYER_DETAIL,
                {
                    urlParams: { employerId }
                }
            );
            return data.employer_details;
        } catch (error) {
            console.error('Error fetching employer details:', error);
            throw new Error("Failed to fetch employer details");
        }
    },

    /**
     * Get issues summary for an employer
     */
    async getEmployerIssues(employerId: number): Promise<OperationalIssueSummary> {
        try {
            return await ApiService.request<OperationalIssueSummary>(
                EndpointKeys.GET_EMPLOYER_ISSUES,
                {
                    queryParams: { employer_id: employerId }
                }
            );
        } catch (error) {
            console.error('Error fetching employer issues:', error);
            throw new Error("Failed to fetch employer issues");
        }
    },

    /**
     * Get all active issues grouped by type
     */
    async getActiveIssues(): Promise<OperationalIssueDetail[]> {
        try {
            return await ApiService.request<OperationalIssueDetail[]>(
                EndpointKeys.GET_ACTIVE_ISSUES
            );
        } catch (error) {
            console.error('Error fetching active issues:', error);
            throw new Error("Failed to fetch active issues");
        }
    },

    /**
     * Get all active issues grouped by category
     */
    async getIssuesByCategory(): Promise<IssuesByCategory> {
        try {
            return await ApiService.request<IssuesByCategory>(
                EndpointKeys.GET_ISSUES_BY_CATEGORY
            );
        } catch (error) {
            console.error('Error fetching issues by category:', error);
            throw new Error("Failed to fetch issues by category");
        }
    },

    /**
     * Get detailed info for a specific issue
     */
    async getIssueDetail(issueId: number): Promise<IssueDetail> {
        try {
            const data = await ApiService.request<{ issue_detail: IssueDetail }>(
                EndpointKeys.GET_ISSUE_DETAIL,
                {
                    urlParams: { issueId }
                }
            );
            return data.issue_detail;
        } catch (error) {
            console.error('Error fetching issue details:', error);
            throw new Error("Failed to fetch issue details");
        }
    },

    /**
     * Override an issue with justification
     */
    async overrideIssue(issueId: number, requestData: OverrideIssueRequest): Promise<IssueDetail> {
        try {
            const data = await ApiService.request<{ issue_detail: IssueDetail }>(
                EndpointKeys.OVERRIDE_ISSUE,
                {
                    method: "POST",
                    body: requestData,
                    urlParams: { issueId }
                }
            );
            return data.issue_detail;
        } catch (error) {
            console.error('Error overriding issue:', error);
            throw new Error("Failed to override issue");
        }
    },

    /**
     * Get all affiliate partners with their referral and payment data
     */
    async getAffiliatePartners(): Promise<AffiliatePartner[]> {
        try {
            const data = await ApiService.request<AffiliatePartnersResponse>(
                EndpointKeys.GET_AFFILIATE_PARTNERS
            );
            return data.affiliates;
        } catch (error) {
            console.error('Error fetching affiliate partners:', error);
            throw new Error("Failed to fetch affiliate partners");
        }
    },

    /**
     * Get all implementation employers
     */
    async getImplementationEmployers(): Promise<ImplementationEmployer[]> {
        try {
            const data = await ApiService.request<ImplementationEmployersResponse>(
                EndpointKeys.GET_IMPLEMENTATION_EMPLOYERS
            );
            return data.implementation_employers;
        } catch (error) {
            console.error('Error fetching implementation employers:', error);
            throw new Error("Failed to fetch implementation employers");
        }
    },

    /**
     * Get all features with their status
     */
    async getFeatures(): Promise<Feature[]> {
        try {
            const data = await ApiService.request<{ features: Feature[] }>(
                EndpointKeys.GET_FEATURES
            );
            return data.features;
        } catch (error) {
            console.error('Error fetching features:', error);
            throw new Error("Failed to fetch features");
        }
    },

    /**
     * Create or update an onboarding step override for an employer
     */
    async createOnboardingOverride(employerId: number, requestData: CreateOverrideRequest): Promise<OnboardingStepOverride> {
        try {
            const data = await ApiService.request<{ override: OnboardingStepOverride }>(
                EndpointKeys.CREATE_ONBOARDING_OVERRIDE,
                {
                    method: "POST",
                    body: requestData,
                    urlParams: { employerId }
                }
            );
            return data.override;
        } catch (error) {
            console.error('Error creating onboarding override:', error);
            throw new Error("Failed to create onboarding override");
        }
    },

    /**
     * Remove an onboarding step override for an employer
     */
    async removeOnboardingOverride(employerId: number, stepType: string): Promise<void> {
        try {
            await ApiService.request<{ message: string }>(
                EndpointKeys.REMOVE_ONBOARDING_OVERRIDE,
                {
                    method: "DELETE",
                    urlParams: { employerId, stepType }
                }
            );
        } catch (error) {
            console.error('Error removing onboarding override:', error);
            throw new Error("Failed to remove onboarding override");
        }
    },

    /**
     * Get all active overrides for an employer
     */
    async getEmployerOverrides(employerId: number): Promise<OnboardingStepOverride[]> {
        try {
            const data = await ApiService.request<{ overrides: OnboardingStepOverride[] }>(
                EndpointKeys.GET_EMPLOYER_OVERRIDES,
                {
                    urlParams: { employerId }
                }
            );
            return data.overrides;
        } catch (error) {
            console.error('Error fetching employer overrides:', error);
            throw new Error("Failed to fetch employer overrides");
        }
    },

    /**
     * Get all available onboarding step types for override creation
     */
    async getAvailableOverrideStepTypes(): Promise<OnboardingStepType[]> {
        try {
            const data = await ApiService.request<{ step_types: OnboardingStepType[] }>(
                EndpointKeys.GET_AVAILABLE_OVERRIDE_STEP_TYPES
            );
            return data.step_types;
        } catch (error) {
            console.error('Error fetching available override step types:', error);
            throw new Error("Failed to fetch available override step types");
        }
    },

    /**
     * Get demo accounts data with domain filtering applied
     */
    async getDemoAccounts(includePurged: boolean = true, emailsOnly: boolean = true): Promise<DemoAccountsSummary | DemoEmailList> {
        try {
            const data = await ApiService.request<DemoAccountsResponse>(
                EndpointKeys.GET_DEMO_ACCOUNTS,
                {
                    queryParams: {
                        include_purged: includePurged.toString(),
                        emails_only: emailsOnly.toString()
                    }
                }
            );
            return data.demo_data;
        } catch (error) {
            console.error('Error fetching demo accounts:', error);
            throw new Error("Failed to fetch demo accounts");
        }
    },
}; 